import { useQuery } from '@tanstack/react-query';
import { Card, CardHeader, CardBody, Label } from '@repo/ui';
import { api } from '@xtnd/web/shared/api';

type PaymentLinkSubjectMode = 'bill' | 'contact';

interface CustomFieldMapping {
  fieldId?: number;
  fieldName?: string;
  fieldType?: 'text' | 'dropdown' | 'picklist' | 'textarea';
  sampleValues?: string[];
}

interface BankAccountCustomFieldMappingProps {
  config: {
    /** Legacy key — surfaced as the Trust mapping when trustBankAccountCustomFieldMapping isn't set. */
    bankAccountCustomFieldMapping?: CustomFieldMapping;
    /** Picks the trust bank account from a matter custom field for trust payment links / TRR creation. */
    trustBankAccountCustomFieldMapping?: CustomFieldMapping;
    /** Picks the operating bank account from a matter custom field for operational payment links. */
    operationalBankAccountCustomFieldMapping?: CustomFieldMapping;
    defaultOperationalDirectBankAccountId?: string;
    defaultTrustBankAccountId?: string;
    allowPartialPayments?: boolean;
    paymentLinkSubjectMode?: PaymentLinkSubjectMode;
  };
  onConfigChange: (updates: Partial<{
    bankAccountCustomFieldMapping?: CustomFieldMapping;
    trustBankAccountCustomFieldMapping?: CustomFieldMapping;
    operationalBankAccountCustomFieldMapping?: CustomFieldMapping;
    defaultOperationalDirectBankAccountId?: string;
    defaultTrustBankAccountId?: string;
    allowPartialPayments?: boolean;
    paymentLinkSubjectMode?: PaymentLinkSubjectMode;
  }>) => void;
}

interface ClioCustomField {
  id: number;
  etag: string;
  field_name: string;
  name: string;
  field_type: 'text' | 'number' | 'date' | 'checkbox' | 'dropdown' | 'picklist' | 'textarea';
  field_required: boolean;
  field_displayed: boolean;
  field_display_order: number[];
  parent_type: 'Matter' | 'Contact' | 'Activity';
  created_at: string;
  updated_at: string;
  soft_deleted?: boolean;
  dropdown_options?: string[];
  picklist_options?: Array<{
    id: number;
    option: string;
  }>;
}

interface BankAccount {
  id: number;
  name: string;
  account_number?: string;
  type: 'Trust' | 'Operating';
  currency?: string;
}

export function BankAccountCustomFieldMapping({
  config,
  onConfigChange
}: BankAccountCustomFieldMappingProps) {
  // Fetch matter custom fields
  const { data: customFields = [], isLoading: fieldsLoading } = useQuery<ClioCustomField[]>({
    queryKey: ['clio-matter-custom-fields'],
    queryFn: async () => {
      const response = await api.get<{ data: ClioCustomField[] }>('/clio/matter-custom-fields-list');
      return (response.data || []).filter(Boolean); // Ensure no undefined items
    },
    retry: 2,
    retryDelay: 1000,
  });

  // Fetch bank accounts
  const { data: bankAccounts = [], isLoading: accountsLoading } = useQuery<BankAccount[]>({
    queryKey: ['clio-bank-accounts'],
    queryFn: async () => {
      const response = await api.get<{ data: BankAccount[] }>('/clio/bank-accounts');
      return (response.data || []).filter(Boolean); // Ensure no undefined items
    },
    retry: 2,
    retryDelay: 1000,
  });

  const buildMapping = (fieldId: string): CustomFieldMapping | undefined => {
    const selectedFieldId = parseInt(fieldId);
    const selectedField = Array.isArray(customFields) ? (customFields || []).find(f => f?.id === selectedFieldId) : undefined;
    if (!selectedField || !selectedField.id || !selectedField.name || !selectedField.field_type) return undefined;
    const supportedFieldTypes: ('text' | 'dropdown' | 'picklist' | 'textarea')[] = ['text', 'dropdown', 'picklist', 'textarea'];
    const fieldType = supportedFieldTypes.includes(selectedField.field_type as 'text' | 'dropdown' | 'picklist' | 'textarea')
      ? (selectedField.field_type as 'text' | 'dropdown' | 'picklist' | 'textarea')
      : undefined;
    if (!fieldType) return undefined;
    let sampleValues: string[] = [];
    if (selectedField.dropdown_options && Array.isArray(selectedField.dropdown_options)) {
      sampleValues = selectedField.dropdown_options;
    } else if (selectedField.picklist_options && Array.isArray(selectedField.picklist_options)) {
      sampleValues = selectedField.picklist_options.map(opt => opt.option || '').filter(Boolean);
    }
    return { fieldId: selectedField.id, fieldName: selectedField.name, fieldType, sampleValues };
  };

  // Trust mapping: prefer the explicit trust key, fall back to the legacy
  // bankAccountCustomFieldMapping for tenants that haven't migrated yet.
  // Writes go to trustBankAccountCustomFieldMapping only — the API tenant config
  // service falls back to the legacy field when reading, so existing tenants
  // keep working until their next save.
  const trustMapping = config.trustBankAccountCustomFieldMapping ?? config.bankAccountCustomFieldMapping;
  const operationalMapping = config.operationalBankAccountCustomFieldMapping;

  const handleTrustFieldChange = (fieldId: string) => {
    onConfigChange({ trustBankAccountCustomFieldMapping: buildMapping(fieldId) });
  };
  const handleOperationalFieldChange = (fieldId: string) => {
    onConfigChange({ operationalBankAccountCustomFieldMapping: buildMapping(fieldId) });
  };

  return (
    <Card className="border-xtnd-light">
      <CardHeader>
        <h2 className="text-xl font-semibold text-xtnd-dark dark:text-xtnd-white">
          Bank and Payment Configuration
        </h2>
        <p className="text-sm text-xtnd-dark-500 dark:text-xtnd-dark-300 mt-1">
          Configure how bank accounts are selected for Clio Payment Links across
          Operational Direct, Trust Request, and Evergreen flows using the same
          resolution order.
        </p>
      </CardHeader>
      <CardBody className="space-y-4">
        {/* Allow Partial Payments */}
        <div className="flex items-center justify-between rounded-md border border-xtnd-dark-100 dark:border-xtnd-dark-500 p-4 bg-xtnd-dark-50 dark:bg-xtnd-dark-700/50">
          <div>
            <Label htmlFor="allowPartialPayments" className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">
              Allow Partial Payments
            </Label>
            <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
              When enabled, payment links do not pre-fill the amount so payers can enter any amount (partial or full).
            </p>
          </div>
          <input
            id="allowPartialPayments"
            type="checkbox"
            checked={config.allowPartialPayments ?? false}
            onChange={(e) => onConfigChange({ allowPartialPayments: e.target.checked })}
            className="h-4 w-4 rounded border-xtnd-dark-200 text-xtnd-blue focus:ring-xtnd-blue"
          />
        </div>

        {/* Payment Link Subject Mode */}
        <div className="rounded-md border border-xtnd-dark-100 dark:border-xtnd-dark-500 p-4 bg-xtnd-dark-50 dark:bg-xtnd-dark-700/50">
          <Label className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">
            Payment Link Subject
          </Label>
          <p className="text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300 mt-1">
            Choose how Juniper payment links attach to Clio. <strong>Bill</strong> creates a link for the specific invoice; <strong>Contact</strong> creates a link the client can use to pay any of their outstanding invoices.
          </p>
          <div className="mt-3 flex items-center gap-6">
            <label className="flex items-center gap-2 text-sm text-xtnd-dark-600 dark:text-xtnd-dark-200">
              <input
                type="radio"
                name="paymentLinkSubjectMode"
                value="bill"
                checked={(config.paymentLinkSubjectMode ?? 'bill') === 'bill'}
                onChange={() => onConfigChange({ paymentLinkSubjectMode: 'bill' })}
                className="h-4 w-4 text-xtnd-blue focus:ring-xtnd-blue"
              />
              Bill
            </label>
            <label className="flex items-center gap-2 text-sm text-xtnd-dark-600 dark:text-xtnd-dark-200">
              <input
                type="radio"
                name="paymentLinkSubjectMode"
                value="contact"
                checked={config.paymentLinkSubjectMode === 'contact'}
                onChange={() => onConfigChange({ paymentLinkSubjectMode: 'contact' })}
                className="h-4 w-4 text-xtnd-blue focus:ring-xtnd-blue"
              />
              Contact
            </label>
          </div>
        </div>

        {/* Custom Field Mapping — Trust */}
        <div>
          <Label htmlFor="trustBankAccountField" className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">
            Matter Custom Field — Trust Bank Account
          </Label>
          <select
            id="trustBankAccountField"
            value={trustMapping?.fieldId || ''}
            onChange={(e) => handleTrustFieldChange(e.target.value)}
            disabled={fieldsLoading}
            className="mt-1 block w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md shadow-sm focus:outline-none focus:ring-xtnd-blue focus:border-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
          >
            <option value="">
              {fieldsLoading ? 'Loading custom fields...' : 'Select custom field...'}
            </option>
            {Array.isArray(customFields) ? (customFields || [])
              .filter(field => field?.parent_type === 'Matter' && !field?.soft_deleted)
              .map(field => (
                <option key={field.id} value={field.id}>
                  {field.name} ({field.field_type})
                </option>
              )) : []}
          </select>
          <p className="mt-1 text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
            Used for trust payment links and Trust Request creation. Picklist values should match Trust bank-account names. Leave unset to always use the Default Trust Account.
          </p>
          {trustMapping?.sampleValues &&
           Array.isArray(trustMapping.sampleValues) &&
           trustMapping.sampleValues.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">
                Sample values: {trustMapping.sampleValues.slice(0, 3).join(', ')}
                {trustMapping.sampleValues.length > 3 && '...'}
              </p>
            </div>
          )}
        </div>

        {/* Custom Field Mapping — Operational */}
        <div>
          <Label htmlFor="operationalBankAccountField" className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">
            Matter Custom Field — Operating Bank Account
          </Label>
          <select
            id="operationalBankAccountField"
            value={operationalMapping?.fieldId || ''}
            onChange={(e) => handleOperationalFieldChange(e.target.value)}
            disabled={fieldsLoading}
            className="mt-1 block w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md shadow-sm focus:outline-none focus:ring-xtnd-blue focus:border-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
          >
            <option value="">
              {fieldsLoading ? 'Loading custom fields...' : 'Select custom field...'}
            </option>
            {Array.isArray(customFields) ? (customFields || [])
              .filter(field => field?.parent_type === 'Matter' && !field?.soft_deleted)
              .map(field => (
                <option key={field.id} value={field.id}>
                  {field.name} ({field.field_type})
                </option>
              )) : []}
          </select>
          <p className="mt-1 text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
            Used for operational payment links. Picklist values should match Operating bank-account names. Leave unset to always use the Default Operational Direct Account.
          </p>
          {operationalMapping?.sampleValues &&
           Array.isArray(operationalMapping.sampleValues) &&
           operationalMapping.sampleValues.length > 0 && (
            <div className="mt-2">
              <p className="text-xs text-xtnd-dark-500 dark:text-xtnd-dark-300">
                Sample values: {operationalMapping.sampleValues.slice(0, 3).join(', ')}
                {operationalMapping.sampleValues.length > 3 && '...'}
              </p>
            </div>
          )}
        </div>

        {/* Default Bank Accounts */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="defaultOperationalAccount" className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">
              Default Operational Direct Account
            </Label>
            <select
              id="defaultOperationalAccount"
              value={config.defaultOperationalDirectBankAccountId || ''}
              onChange={(e) => onConfigChange({
                defaultOperationalDirectBankAccountId: e.target.value
              })}
              disabled={accountsLoading}
              className="mt-1 block w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md shadow-sm focus:outline-none focus:ring-xtnd-blue focus:border-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
            >
              <option value="">
                {accountsLoading ? 'Loading accounts...' : 'Select default account...'}
              </option>
              {Array.isArray(bankAccounts) ? (bankAccounts || [])
                .filter(account => account?.type === 'Operating')
                .map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.account_number || 'No number'})
                  </option>
                )) : []}
            </select>
            <p className="mt-1 text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
              Used for Operational Direct transactions when custom field is empty
            </p>
          </div>

          <div>
            <Label htmlFor="defaultTrustAccount" className="text-sm font-medium text-xtnd-dark-600 dark:text-xtnd-dark-200">
              Default Trust Account
            </Label>
            <select
              id="defaultTrustAccount"
              value={config.defaultTrustBankAccountId || ''}
              onChange={(e) => onConfigChange({
                defaultTrustBankAccountId: e.target.value
              })}
              disabled={accountsLoading}
              className="mt-1 block w-full px-3 py-2 border border-xtnd-dark-200 dark:border-xtnd-dark-500 rounded-md shadow-sm focus:outline-none focus:ring-xtnd-blue focus:border-xtnd-blue bg-white dark:bg-xtnd-dark-700 text-xtnd-dark dark:text-xtnd-white"
            >
              <option value="">
                {accountsLoading ? 'Loading accounts...' : 'Select default account...'}
              </option>
              {Array.isArray(bankAccounts) ? (bankAccounts || [])
                .filter(account => account?.type === 'Trust')
                .map(account => (
                  <option key={account.id} value={account.id}>
                    {account.name} ({account.account_number || 'No number'})
                  </option>
                )) : []}
            </select>
            <p className="mt-1 text-xs text-xtnd-dark-400 dark:text-xtnd-dark-300">
              Used for Trust Request and Evergreen transactions when custom field is empty
            </p>
          </div>
        </div>

        {/* Fallback Logic Explanation */}
        <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-md border border-blue-200 dark:border-blue-800">
          <div className="flex items-start space-x-3">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </div>
            <div>
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                Bank Account Resolution Order
              </h4>
              <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-1">
                <li><strong>1.</strong> Matter custom field value, scoped per transaction type:
                  <ul className="ml-4 mt-1 space-y-1">
                    <li>• Operational → Operating Bank Account custom field (if configured)</li>
                    <li>• Trust Request / Evergreen → Trust Bank Account custom field (if configured)</li>
                  </ul>
                </li>
                <li><strong>2.</strong> Default account for the transaction type:
                  <ul className="ml-4 mt-1 space-y-1">
                    <li>• Operational → Default Operational Direct Account</li>
                    <li>• Trust Request / Evergreen → Default Trust Account</li>
                  </ul>
                </li>
                <li><strong>3.</strong> Clio's default account for the type</li>
                <li><strong>4.</strong> Transaction fails with "Missing Configured Bank Account"</li>
              </ol>
              <p className="text-xs text-blue-700 dark:text-blue-300 mt-2">
                A custom-field value resolving to the wrong account type (e.g., a Trust account picked for an operational link) is rejected by Clio; the resolver falls through to step 2.
              </p>
            </div>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
